'use client'

import React, { ReactNode, useEffect, useState, useRef } from "react"
import HolidayStayCard from '@/features/holiday-stay/Card'
import LeisureActivityCard from '@/features/holiday-stay/LeisureActivityCard'
import BafadCard from '@/features/bafad/Card'
import JobCard from '@/features/jobs/Card'
import SchoolThematicCard from '@/features/school/ThematicCard'
import SchoolActivityCard from '@/features/school/ActivityCard'
import SchoolStayCard from '@/features/school/StayCard'
import PressArticleCard from '@/features/press-article/Card'
import PostCard from '@/features/post/Card'
import PublicationCard from '@/features/publication/Card'
import EducationCourseCard from "@/features/education-course/Card"
import AccomodationCard from '@/features/accomodation/Card'
import { useInView } from 'react-intersection-observer'
import Loading from "@/components/loading/Loading"
import { getSearchFnByObject } from "@/services/search"
import moment from "moment"

type ResultsListProps = {
    aroeven: Aroeven,
    object: string,
    initialResults: Searchable[],
    initialCount: number,
    searchParams?: any
}

// saved lists older than this are not restored
const MAX_AGE = 10 * 60 * 1000

let reloadChecked = false

// true only for the first list mounted after a browser reload of this very page
function isReload(): boolean {
    if (reloadChecked) return false
    reloadChecked = true
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    return navigation?.type === 'reload' && navigation.name === window.location.href
}

export default function ResultsList({
    aroeven,
    object,
    initialResults = [],
    initialCount,
    searchParams
}: ResultsListProps) {

    const init = useRef(false)
    // when the displayed data came from the server
    const fetchedAt = useRef(Date.now())

    const { ref, inView } = useInView()
    //const searchParams = useSearchParams()

    /*const [results, setResults] = useState((console.log('usestate') !== null && typeof window === 'undefined') ? initialResults : ('results_list' in window.history.state ? window.history.state.results_list.results : initialResults))
    const [page, setPage] = useState(typeof window === 'undefined' ? 1 : ('results_list' in window.history.state ? window.history.state.results_list.page : 1))
    const [count, setCount] = useState(typeof window === 'undefined' ? initialCount : ('results_list' in window.history.state ? window.history.state.results_list.count : initialCount))*/
    const [results, setResults] = useState(initialResults)
    const [page, setPage] = useState(1)
    const [count, setCount] = useState(initialCount)
    const [addedSearchParams, setAddedSearchParams] = useState<any>({})

    const getMoreResults = async () => {
        const next = page + 1

        const searchFn = getSearchFnByObject(object)

        const { items, count } = await searchFn(aroeven, {
            ...searchParams,
            page: next
        })
        setCount(count)
        setResults([
            ...results,
            ...items
        ])
        setPage(next)
    }

    useEffect(() => {
        if (inView) {
            getMoreResults()
        }
    }, [inView])

    /*useEffect(() => {
        //if (init.current === false || typeof window === 'undefined') return
        setResults(initialResults)
    }, [searchParams])*/

    useEffect(() => {
        if (init.current === false) return
        try {
            window.history.replaceState({
                ...history.state,
                results_list: {
                    results,
                    count,
                    page,
                    fetchedAt: fetchedAt.current
                }
            }, '')
        }
        catch (error) {}
    }, [results])

    useEffect(() => {
        if (typeof window === 'undefined') return
        if ('results_list' in window.history.state === false) return
        const saved = window.history.state.results_list
        if (isReload() || Date.now() - (saved.fetchedAt ?? 0) > MAX_AGE) {
            // fresh data wanted : drop the saved list instead of restoring it
            const { results_list, ...state } = window.history.state
            window.history.replaceState(state, '')
            return
        }
        // keeps the original date, otherwise the saved list would never expire
        fetchedAt.current = saved.fetchedAt
        setResults(saved.results)
        setPage(saved.page)
        setCount(saved.count)
    }, [])

    useEffect(() => {
        if (init.current === false) {
            init.current = true
        }
    }, [])

    return (
        <>
            {results.map((result: any, indexResult: number) => {
                return (
                    <React.Fragment key={indexResult}>
                        <div className={(['HolidayStay', 'Bafad', 'SchoolStay', 'SchoolActivity', 'SchoolThematic', 'Job', 'Publication'].includes(object)) ? "col-sm-6 col-xl-4" : "col-sm-6 col-lg-4 col-xl-3"}>
                            <div className={object === 'SchoolThematic' ? '' : "vignette h-100"}>
                                {object === 'HolidayStay' &&
                                    <HolidayStayCard
                                        holidayStay={result}
                                        filters={{
                                            ...searchParams?.minDate && moment(searchParams.minDate, 'YYYY-MM-DD').isValid() && {
                                                minDate: searchParams.minDate
                                            },
                                            ...searchParams?.maxDate && moment(searchParams.maxDate, 'YYYY-MM-DD').isValid() && {
                                                maxDate: searchParams.maxDate
                                            }
                                        }}
                                    />
                                }
                                {object === 'LeisureActivity' &&
                                    <LeisureActivityCard
                                        leisureActivity={result}
                                    />
                                }
                                {object === 'Bafad' &&
                                    <BafadCard
                                        bafad={result}
                                    />
                                }
                                {object === 'EducationCourse' &&
                                    <EducationCourseCard
                                        educationCourse={result as EducationCourse}
                                    />
                                }
                                {object === 'SchoolThematic' &&
                                    <SchoolThematicCard
                                        schoolThematic={result}
                                    />
                                }
                                {object === 'SchoolActivity' &&
                                    <SchoolActivityCard
                                        schoolActivity={result}
                                    />
                                }
                                {object === 'SchoolStay' &&
                                    <SchoolStayCard
                                        accomodation={result}
                                    />
                                }
                                {object === 'Accomodation' &&
                                    <AccomodationCard
                                        accomodation={result}
                                    />
                                }
                                {object === 'Job' &&
                                    <JobCard
                                        job={result}
                                    />
                                }
                                {object === 'PressArticle' &&
                                    <PressArticleCard
                                        aroeven={aroeven}
                                        pressArticle={result}
                                    />
                                }
                                {object === 'Post' &&
                                    <PostCard
                                        aroeven={aroeven}
                                        post={result}
                                    />
                                }
                                {object === 'Publication' &&
                                    <PublicationCard
                                        aroeven={aroeven}
                                        publication={result}
                                    />
                                }
                            </div>
                        </div>
                    </React.Fragment>
                )
            })}
            {results.length < count &&
                <div ref={ref}>
                    <Loading />
                </div>
            }
            {/*{results.length >= Math.min(count, 24) && results.length < count &&
                <h1>Voir plus</h1>
            }*/}
        </>
    )
}